import { addDays, startOfDay } from 'date-fns';
import pluralize from 'pluralize';
import { type ReactNode, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { FreePlanGate } from '@/modules/billing/components/FreePlanGate';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { useCompanyStatsQuery } from '@/modules/companies/queries/useCompanyStatsQuery';
import { FreePlanAutomationCta } from '@/modules/hiring-manager/components/freePlanAutomationCta/FreePlanAutomationCta';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import {
  PortalList,
  PortalListSkeleton,
} from '@/modules/hiring-manager/components/PortalList';
import { toSearchSuffix } from '@/modules/hiring-manager/utils/searchSuffix';
import { getHMTenantSelection } from '@/modules/hiring-manager/utils/tenant';
import { thisWeek } from '@/modules/hiring-manager/utils/week';
import { isJobVisibleToHiringManagers } from '@/modules/jobs/jobStates';
import { useJobsQuery } from '@/modules/jobs/queries/useJobsQuery';
import { useResendMessagesMutation } from '@/modules/messaging/mutations/useResendMessagesMutation';
import {
  OUTREACH_THREADS_PAGE_SIZE,
  useFailedOutreachThreadsQuery,
  useOutreachThreadsQuery,
} from '@/modules/messaging/queries/useOutreachThreadsQuery';
import { useDebouncedValue } from '@/modules/search/hooks/useDebouncedValue';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import {
  SendingLimitsSummary,
  SendingPausedNotice,
} from '@/modules/user-external-accounts/components/SendingLimits';
import { useUserExternalAccountsQuery } from '@/modules/user-external-accounts/queries/useUserExternalAccountsQuery';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { ConnectLinkedInPrompt } from './ConnectLinkedInPrompt';
import { type AttentionGroup, MessagesAttention } from './MessagesAttention';
import { MessagesEmpty } from './MessagesEmpty';
import { ALL_JOBS, MessagesFilterBar } from './MessagesFilterBar';
import { MessageThreadRow } from './MessageThreadRow';
import {
  failureReason,
  failureReasons,
  latestOutbound,
  type MessageApi,
  type MessageThreadApi,
  type OutreachStatus,
  outreachStatuses,
  reasonCopy,
  STATUS_PARAM,
} from './outreachThread';

/** `to` is exclusive on the API, so a picked end day becomes the next midnight. */
const exclusiveEnd = (date: Date) => addDays(startOfDay(date), 1).toISOString();

function attentionGroups(threads: MessageThreadApi[]): AttentionGroup[] {
  const byReason = new Map<string, AttentionGroup>();

  for (const thread of threads) {
    const message = latestOutbound(thread);
    if (message?.status !== 'Failed') continue;
    const reason = failureReason(message);
    const group = byReason.get(reason) ?? { reason, threads: [], messages: [] };
    group.threads.push(thread);
    group.messages.push(message);
    byReason.set(reason, group);
  }

  return failureReasons
    .map((reason) => byReason.get(reason))
    .filter((group): group is AttentionGroup => group !== undefined);
}

/**
 * Every LinkedIn message Finlay sent on the customer's behalf: what failed
 * and why at the top, then the log with API-side filters underneath.
 * Threads belong to the signed-in user's LinkedIn account and are narrowed
 * to the selected tenant, matching the stats and job filter.
 */
export function Outreach() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserQuery = useCurrentUserQuery();
  const tenantSelection = getHMTenantSelection(
    currentUserQuery.data,
    searchParams,
  );
  const companyId = tenantSelection.selectedTenantId;

  // In the URL so the job pipeline can link straight to the failures.
  const status = outreachStatuses.find(
    (value) => value === searchParams.get(STATUS_PARAM),
  );
  const setStatus = (next: OutreachStatus | undefined) =>
    setSearchParams(
      (params) => {
        if (next) params.set(STATUS_PARAM, next);
        else params.delete(STATUS_PARAM);
        return params;
      },
      { replace: true },
    );
  const [jobId, setJobId] = useState(ALL_JOBS);
  const [range, setRange] = useState<DateRange | undefined>();
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useDebouncedValue(search, setSearch);

  const userId = currentUserQuery.data?.id;
  const externalAccountsQuery = useUserExternalAccountsQuery(userId);
  const companyQuery = useCompanyQuery(companyId ?? '', {
    enabled: Boolean(companyId),
  });
  // A thread belongs to the account that sent it, so without a LinkedIn
  // account there is nothing to read. A connected-but-broken account still
  // has threads, and its failures are what the attention section is for.
  const linkedInAccount = externalAccountsQuery.data?.find(
    (account) => account.type === 'LinkedIn',
  );
  const hasLinkedInAccount = externalAccountsQuery.data?.some(
    (account) => account.type === 'LinkedIn',
  );

  const statsQuery = useCompanyStatsQuery(companyId, thisWeek());
  const jobsQuery = useJobsQuery({
    ...(companyId ? { companyId } : {}),
  });
  const failedQuery = useFailedOutreachThreadsQuery(
    companyId,
    hasLinkedInAccount === true,
  );
  const feed = useOutreachThreadsQuery(
    {
      tenantId: companyId,
      ...(search ? { search } : {}),
      ...(status ? { status } : {}),
      ...(jobId !== ALL_JOBS ? { jobId } : {}),
      ...(range?.from ? { from: startOfDay(range.from).toISOString() } : {}),
      ...(range?.to ? { to: exclusiveEnd(range.to) } : {}),
    },
    hasLinkedInAccount === true && Boolean(companyId),
  );

  const resend = useResendMessagesMutation();
  // The rows leaving "Needs attention" are the confirmation.
  const resendMessages = (messageIds: string[]) =>
    resend.mutate(messageIds, {
      onError: (error) =>
        toast.error(actionErrorMessage('resend the messages', error)),
    });
  const jobs = (jobsQuery.data ?? [])
    .filter((job) => isJobVisibleToHiringManagers(job.state))
    .map((job) => ({ id: job.id, title: job.title }));
  const draftJob = jobsQuery.data?.find(
    (job) => job.sourcingState === 'Draft' && job.ownerId === userId,
  );
  const groups = attentionGroups(failedQuery.data ?? []);
  // Every failure the customer has to act on: reconnect or resend.
  const needsYou = groups
    .filter((group) => reasonCopy[group.reason].action !== null)
    .reduce((sum, group) => sum + group.threads.length, 0);
  // Links to other pages keep the tenant selection but not this filter.
  const linkParams = new URLSearchParams(searchParams);
  linkParams.delete(STATUS_PARAM);
  const searchSuffix = toSearchSuffix(linkParams);
  const stats = statsQuery.data;
  const hasFilters =
    status !== undefined ||
    jobId !== ALL_JOBS ||
    range !== undefined ||
    searchInput !== '';
  const clearFilters = () => {
    setStatus(undefined);
    setJobId(ALL_JOBS);
    setRange(undefined);
    setSearchInput('');
  };
  const hasNoLiveJobs =
    jobsQuery.isSuccess &&
    !(jobsQuery.data ?? []).some((job) => job.state === 'Published');
  const draftJobHref =
    hasNoLiveJobs && draftJob
      ? `/hm/jobs/${draftJob.id}/edit${searchSuffix}`
      : undefined;
  const needsLinkedInReconnect =
    linkedInAccount?.status === 'Disconnected' ||
    linkedInAccount?.status === 'Failed';
  const integrationsHref =
    companyQuery.data?.isAllowedToConnectLinkedIn && companyId
      ? `/hm/integrations${tenantSelection.tenantSearch}`
      : undefined;

  if (currentUserQuery.isPending || externalAccountsQuery.isPending) {
    return (
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-72" />
        </div>
        <PortalListSkeleton rows={4} />
      </div>
    );
  }

  const freePlanCta = (
    <FreePlanAutomationCta
      surface="messages"
      to={`/hm/billing${searchSuffix}`}
    />
  );
  const messagesEmpty = (
    <MessagesEmpty
      addJobHref={`/hm/chat${searchSuffix}`}
      draftJobHref={draftJobHref}
      hasFilters={hasFilters}
      hasNoLiveJobs={hasNoLiveJobs}
      integrationsHref={integrationsHref}
      linkedInStatus={linkedInAccount?.status}
      onClearFilters={clearFilters}
    />
  );

  return (
    <div className="flex flex-col gap-10">
      <div>
        <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em]">
          Outreach
        </h1>
        <p className="text-muted-foreground mt-1.5">
          <span>
            {stats && hasLinkedInAccount ? (
              <>
                This week Finlay reached{' '}
                <Strong>
                  {stats.peopleReached}{' '}
                  {pluralize('person', stats.peopleReached)}
                </Strong>{' '}
                on LinkedIn and <Strong>{stats.peopleReplied} replied</Strong>.
              </>
            ) : (
              'Sent messages and replies.'
            )}
            {hasLinkedInAccount &&
              failedQuery.isSuccess &&
              (needsYou > 0 ? (
                <>
                  {' '}
                  <span className="font-medium text-red-700 tabular-nums">
                    {needsYou} {pluralize('message', needsYou)}
                  </span>{' '}
                  need you.
                </>
              ) : (
                ' No action needed.'
              ))}
          </span>
        </p>
        {linkedInAccount && (
          <div className="mt-1">
            <SendingLimitsSummary accountId={linkedInAccount.id} />
          </div>
        )}
      </div>

      {externalAccountsQuery.isError ? (
        <HMLoadError
          description="We could not check your LinkedIn connection. Check your connection and try again."
          isRetrying={externalAccountsQuery.isFetching}
          onRetry={() => void externalAccountsQuery.refetch()}
          title="Outreach could not be loaded"
        />
      ) : !hasLinkedInAccount && companyQuery.isPending ? (
        <PortalListSkeleton rows={1} />
      ) : !hasLinkedInAccount && companyQuery.isError ? (
        <HMLoadError
          description="We could not check whether this workspace can connect LinkedIn. Check your connection and try again."
          isRetrying={companyQuery.isFetching}
          onRetry={() => void companyQuery.refetch()}
          title="LinkedIn setup could not be loaded"
        />
      ) : !hasLinkedInAccount ? (
        <FreePlanGate requires="outreach" upgrade={freePlanCta}>
          <ConnectLinkedInPrompt integrationsHref={integrationsHref} />
        </FreePlanGate>
      ) : (
        <>
          {linkedInAccount && (
            <SendingPausedNotice accountId={linkedInAccount.id} />
          )}
          <section
            aria-labelledby="messages-attention"
            className="flex flex-col gap-2.5"
          >
            <SectionHeader
              count={groups.length}
              id="messages-attention"
              title="Needs attention"
            />
            {failedQuery.isError ? (
              <HMLoadError
                description="We could not check whether any messages failed. Check your connection and try again."
                isRetrying={failedQuery.isFetching}
                onRetry={() => void failedQuery.refetch()}
                title="Failed messages could not be loaded"
              />
            ) : (
              <MessagesAttention
                groups={groups}
                integrationsHref={integrationsHref}
                isPending={failedQuery.isPending}
                isResending={resend.isPending}
                onResend={(messages: MessageApi[]) =>
                  resendMessages(messages.map((message) => message.id))
                }
              />
            )}
          </section>

          <section
            aria-labelledby="messages-log"
            className="flex flex-col gap-3"
          >
            <SectionHeader
              action={
                feed.isSuccess && (
                  <span className="text-[13px] text-neutral-500 tabular-nums">
                    {feed.totalCount}{' '}
                    {pluralize('conversation', feed.totalCount)}
                  </span>
                )
              }
              id="messages-log"
              title="All messages"
            />
            <MessagesFilterBar
              hasFilters={hasFilters}
              jobId={jobId}
              jobs={jobs}
              onClearFilters={clearFilters}
              onJobChange={setJobId}
              onRangeChange={setRange}
              onSearchChange={setSearchInput}
              onStatusChange={setStatus}
              range={range}
              search={searchInput}
              status={status}
            />

            {feed.isError ? (
              <HMLoadError
                description="We could not load your outreach. Check your connection and try again."
                isRetrying={feed.isFetching}
                onRetry={() => void feed.refetch()}
                title="Outreach could not be loaded"
              />
            ) : feed.isPending ? (
              <PortalListSkeleton rows={4} />
            ) : feed.threads.length === 0 &&
              !hasFilters &&
              needsLinkedInReconnect &&
              companyQuery.isPending ? (
              <PortalListSkeleton rows={1} />
            ) : feed.threads.length === 0 &&
              !hasFilters &&
              needsLinkedInReconnect &&
              companyQuery.isError ? (
              <HMLoadError
                description="We could not check whether this workspace can reconnect LinkedIn. Check your connection and try again."
                isRetrying={companyQuery.isFetching}
                onRetry={() => void companyQuery.refetch()}
                title="LinkedIn setup could not be loaded"
              />
            ) : feed.threads.length === 0 &&
              !hasFilters &&
              !needsLinkedInReconnect &&
              linkedInAccount?.status !== 'Pending' &&
              jobsQuery.isPending ? (
              <PortalListSkeleton rows={1} />
            ) : feed.threads.length === 0 &&
              !hasFilters &&
              !needsLinkedInReconnect &&
              linkedInAccount?.status !== 'Pending' &&
              jobsQuery.isError ? (
              <HMLoadError
                description="We could not check whether this workspace has any active jobs. Check your connection and try again."
                isRetrying={jobsQuery.isFetching}
                onRetry={() => void jobsQuery.refetch()}
                title="Jobs could not be loaded"
              />
            ) : feed.threads.length === 0 ? (
              // A filter that matches nothing is the user's doing, not the plan's.
              <FreePlanGate
                requires="outreach"
                upgrade={hasFilters ? messagesEmpty : freePlanCta}
              >
                {messagesEmpty}
              </FreePlanGate>
            ) : (
              <div
                aria-busy={feed.isPlaceholderData}
                className={cn(
                  'flex flex-col gap-3 transition-opacity duration-150',
                  feed.isPlaceholderData && 'opacity-60',
                )}
              >
                <PortalList>
                  {feed.threads.map((thread) => (
                    <MessageThreadRow
                      candidateHref={
                        jobId === ALL_JOBS
                          ? undefined
                          : `/hm/jobs/${jobId}/candidates/${thread.candidate.id}${searchSuffix}`
                      }
                      isResending={resend.isPending}
                      key={thread.id}
                      onResend={(message) => resendMessages([message.id])}
                      thread={thread}
                    />
                  ))}
                </PortalList>
                <LoadMore feed={feed} />
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Strong({ children }: { children: ReactNode }) {
  return (
    <span className="text-foreground font-medium tabular-nums">{children}</span>
  );
}

function SectionHeader({
  action,
  count,
  id,
  title,
}: {
  action?: ReactNode;
  count?: number;
  id: string;
  title: string;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <h2 className="text-[13px] leading-[18px] font-semibold" id={id}>
        {title}
      </h2>
      {count !== undefined && count > 0 && (
        <span className="text-[13px] font-medium text-neutral-500 tabular-nums">
          {count}
        </span>
      )}
      {action && <span className="ml-auto">{action}</span>}
    </div>
  );
}

function LoadMore({
  feed,
}: {
  feed: ReturnType<typeof useOutreachThreadsQuery>;
}) {
  const remaining = Math.max(feed.totalCount - feed.threads.length, 0);
  if (!feed.hasNextPage || remaining === 0) return null;
  const step = Math.min(remaining, OUTREACH_THREADS_PAGE_SIZE);
  return (
    <button
      className="hover:text-foreground w-fit cursor-pointer text-xs font-medium text-neutral-500 disabled:opacity-50"
      disabled={feed.isFetchingNextPage}
      onClick={() => void feed.fetchNextPage()}
      type="button"
    >
      Show {step} more
      {remaining > step && ` · ${remaining} hidden`}
      {feed.isFetchingNextPage && '…'}
      <span className="sr-only"> {pluralize('conversation', step)}</span>
    </button>
  );
}
